import React from "react";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { PillVariants } from "src/base-components/Pill";
import {
  getSchemaTypeIcon,
  SchemaType,
} from "src/base-components/SchemaTypeSelector";
import { type Option, Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { CasePropertiesValue } from "src/clients/case-management";
import { CaseCreationNodeMetaMappedValueValueTypeEnum } from "src/clients/flow-api";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";

const STATUS_KEY_SEPARATOR = "#";

const getOptionsForProperty = (property: CasePropertiesValue) => {
  if (property.type === "enum") {
    const options: Option[] = [];

    property.values.forEach((value) => {
      if (value.sub_values && value.sub_values.length > 0) {
        value.sub_values.forEach((subValue) => {
          options.push({
            value: (
              <CaseStatusPill
                status={value.value}
                subStatus={subValue.value}
                variant={value.color as PillVariants}
              />
            ),
            key: `${value.value}${STATUS_KEY_SEPARATOR}${subValue.value}`,
          });
        });
      } else {
        options.push({
          value: (
            <CaseStatusPill
              status={value.value}
              variant={value.color as PillVariants}
            />
          ),
          key: value.value,
        });
      }
    });

    return options;
  }
  return [];
};

type StatusAssignmentCardProps = {
  fieldName: string;
  property: CasePropertiesValue;
  nodeId: string;
  status: string;
  subStatus?: string;
  valueType: CaseCreationNodeMetaMappedValueValueTypeEnum;
  onChangeValueType: (
    valueType: CaseCreationNodeMetaMappedValueValueTypeEnum,
  ) => void;
  onChangeValue: (status: string, subStatus?: string) => void;
  immutable: boolean;
  error?: boolean;
  enableSubStatus?: boolean;
};

export const StatusAssignmentCard: React.FC<StatusAssignmentCardProps> = ({
  nodeId,
  fieldName,
  property,
  status,
  subStatus,
  valueType,
  onChangeValueType,
  onChangeValue,
  immutable,
  error,
  enableSubStatus,
}) => {
  // Construct the current selected key from status and subStatus
  const currentKey = subStatus
    ? `${status}${STATUS_KEY_SEPARATOR}${subStatus}`
    : status;
  const isStatusHighlighted = useIsFieldHighlighted(`${nodeId}:status`);
  const isSubStatusHighlighted = useIsFieldHighlighted(`${nodeId}:sub_status`);

  return (
    <Card className="w-full pt-4 pb-4">
      <SimpleRadioGroup
        className="space-x-4"
        disabled={immutable}
        orientation="horizontal"
        value={
          valueType === CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
            ? "fixed"
            : "dynamic"
        }
        onValueChange={(selectedValue) =>
          onChangeValueType(
            selectedValue === "fixed"
              ? CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
              : CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC,
          )
        }
      >
        <SimpleRadioGroup.Item
          label="Use a predefined case property"
          labelClassName="ml-1.5 font-inter-normal-13px"
          value="fixed"
        />
        <SimpleRadioGroup.Item
          label="Map to a specific data field"
          labelClassName="ml-1.5 font-inter-normal-13px"
          value="dynamic"
        />
      </SimpleRadioGroup>
      {valueType === CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED && (
        <div className="mt-5">
          <Select
            disabled={immutable}
            errored={error}
            options={getOptionsForProperty(property)}
            placeholder={`Select ${fieldName}`}
            value={currentKey}
            fullWidth
            onChange={(selectedKey) => {
              // Parse the key to extract status and subStatus
              const parts = selectedKey.split(STATUS_KEY_SEPARATOR);
              const [status, subStatus] = parts;

              onChangeValue(status, subStatus);
            }}
          />
        </div>
      )}
      {valueType === CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC && (
        <div className="mt-5 flex flex-col gap-y-2">
          <div className="flex w-full items-center gap-2">
            <div className="w-1/2">
              <AutocompleteCodeInput
                disabled={immutable}
                error={error}
                isSearchHighlighted={isStatusHighlighted}
                placeholder="data.field"
                value={status}
                onChange={(selectedValue) => {
                  onChangeValue(selectedValue, subStatus);
                }}
              />
            </div>
            <div className="flex-shrink-0">
              <Icon color="text-gray-500" icon={faArrowRight} size="xs" />
            </div>
            <div className="w-1/2">
              <Input
                placeholder={property.display_name || fieldName}
                prefixIcon={{
                  icon: getSchemaTypeIcon(property.type as SchemaType),
                }}
                disabled
                fullWidth
              />
            </div>
          </div>

          {enableSubStatus && (
            <div className="flex w-full items-center gap-2">
              <div className="w-1/2">
                <AutocompleteCodeInput
                  disabled={immutable}
                  error={error}
                  isSearchHighlighted={isSubStatusHighlighted}
                  placeholder="data.field"
                  value={subStatus || ""}
                  onChange={(selectedValue) => {
                    onChangeValue(status, selectedValue);
                  }}
                />
              </div>
              <div className="flex-shrink-0">
                <Icon color="text-gray-500" icon={faArrowRight} size="xs" />
              </div>
              <div className="w-1/2">
                <Input
                  placeholder="Sub-status"
                  prefixIcon={{
                    icon: getSchemaTypeIcon(property.type as SchemaType),
                  }}
                  disabled
                  fullWidth
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
